<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import BaseModal from "@/components/global-themed/BaseModal.vue";
import {computed, ref} from "vue";
import {useQuery} from "@tanstack/vue-query";
import axios from "axios";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";

const props = defineProps({
  websiteUrl: {
    required: true,
    type: String,
  },
});

const open = ref(false);
const robotsUrl = computed(() => {
  const url = props.websiteUrl;

  if (!url) {
    return null;
  }

  return url.endsWith('/')
    ? `${url}robots.txt`
    : `${url}/robots.txt`;
});

const handleClose = () => {
  open.value = false;
}

const {data: crawlData, isLoading} = useQuery({
  queryKey: ['website-crawl-data'],
  queryFn: async () => {
    const {data} = await axios.get('/ajax/v2/actions/crawl-data');

    return data.data;
  },
});
</script>

<template>
  <DefaultButton
    size="normal"
    class="!tw-px-5 !tw-py-2 tw-h-[42px]"
    @click="open = true"
  >
    <span>View</span>
  </DefaultButton>

  <Teleport
    to=".tailwind"
  >
    <BaseModal
      v-if="open"
      @close="handleClose"
    >
      <div class="tw-px-8 tw-pb-8 tw-flex tw-flex-col tw-gap-12 tw-justify-center">
        <div class="tw-space-y-6">
          <h2 class="tw-text-2.5xl tw-text-grey-dark tw-font-bold">
            Robots.txt File
          </h2>

          <div class="tw-text-lg">
            <p>
              Below is the robots.txt file that we have found on the website
              <a
                class="tw-text-blue tw-underline"
                :href="robotsUrl"
                target="_blank"
              >{{ robotsUrl }}</a>.
            </p>
          </div>
        </div>

        <LoadingBlock v-if="isLoading" />
        <div
          v-else-if="!!crawlData.robots_txt"
          class="tw-flex tw-flex-col tw-gap-6"
        >
          <div
            class="tw-p-6 tw-border-2 tw-border-grey-mid tw-rounded-lg tw-w-full"
          >
            <textarea
              v-model="crawlData.robots_txt"
              rows="10"
              class="tw-w-full tw-text-grey-dark tw-text-lg tw-overflow-scroll visible-scrollbar tw-resize-none"
              readonly
            />
          </div>

          <DefaultButton
            class="tw-w-auto tw-self-end"
            @click="handleClose"
          >
            Close
          </DefaultButton>
        </div>

        <p
          v-else-if="!crawlData.robots_last_crawl"
          class="tw-font-bold tw-text-base"
        >
          We haven't crawled your robots.txt yet, this will be crawled when we next crawl your website.
        </p>

        <p
          v-else
          class="tw-font-bold tw-text-base"
        >
          We could not find your robots.txt file, ensure that your robots.txt file is located at
          <a
            :href="robotsUrl"
            target="_blank"
            class="tw-text-blue tw-underline"
          >
            {{ robotsUrl }}
          </a>
        </p>
      </div>
    </BaseModal>
  </Teleport>
</template>
